import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import CommentSection from "@/components/CommentSection";
import ShareDialog from "@/components/ShareDialog";
import { Play, Pause, Heart, Share2, Flag } from "lucide-react";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

// Helper component to show leaderboard badge for an artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

export default function TrackDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const trackId = new URLSearchParams(location.search).get("id");
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio] = useState(new Audio());
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const { data: track, isLoading } = useQuery({
    queryKey: ["track", trackId],
    queryFn: async () => {
      if (!trackId) return null;
      
      const { data, error } = await supabase
        .from("tracks")
        .select("*")
        .eq("id", trackId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!trackId,
  });

  const { data: feedback } = useQuery({
    queryKey: ["feedback", trackId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback")
        .select(`
          *,
          reviewer:profiles!feedback_reviewer_id_fkey(username, avatar_url)
        `)
        .eq("track_id", trackId);
      if (error) throw error;
      return data;
    },
    enabled: !!trackId,
  });

  const togglePlayPause = async () => {
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (!track?.audio_url) {
        toast.error("Audio URL not available");
        return;
      }
      try {
        audio.src = track.audio_url;
        await audio.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Error playing audio:", error);
        toast.error("Failed to play audio");
      }
    }
  };

  useEffect(() => {
    audio.onended = () => setIsPlaying(false);
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, [audio]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!track) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl text-foreground mb-4">Track Not Found</h1>
          <Button onClick={() => navigate("/Discover")} tooltip="Return to the discover page">Back to Discover</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" tooltip="Go back to previous page">
          ← Back
        </Button>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <img
              src={track.cover_image_url || getDefaultCoverImage(track.id)}
              alt={track.title}
              className="w-full aspect-square object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">{track.title}</h1>
            <p className="text-2xl text-muted-foreground flex items-center gap-2">
              {track.artist_name}
              {track.artist_id && <ArtistLeaderboardBadge artistId={track.artist_id} />}
            </p>
            
            {track.genres && track.genres.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {track.genres.map((genre, idx) => (
                  <span key={idx} className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm">
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {track.description && (
              <p className="text-muted-foreground">{track.description}</p>
            )}

            <div className="flex gap-4 pt-4">
              <Button onClick={togglePlayPause} size="lg" className="flex-1" tooltip={isPlaying ? "Pause track playback" : "Play this track"}>
                {isPlaying ? <Pause className="mr-2 h-5 w-5" /> : <Play className="mr-2 h-5 w-5" />}
                {isPlaying ? "Pause" : "Play"}
              </Button>
              <Button variant="outline" size="lg" tooltip="Like this track">
                <Heart className="h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                tooltip="Share this track"
                onClick={() => setIsShareDialogOpen(true)}
              >
                <Share2 className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" tooltip="Report this track">
                <Flag className="h-5 w-5" />
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{track.praise_count}</div>
                <div className="text-sm text-muted-foreground">Praise</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{track.neutral_count}</div>
                <div className="text-sm text-muted-foreground">Neutral</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{track.scathe_count}</div>
                <div className="text-sm text-muted-foreground">Scathe</div>
              </div>
            </div>
          </div>
        </div>

        {feedback && feedback.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Feedback</h2>
            <div className="space-y-4">
              {feedback.map((item) => (
                <div key={item.id} className="bg-card p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={item.reviewer?.avatar_url || "/placeholder.svg"}
                      alt={item.reviewer?.username}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <div className="font-semibold text-card-foreground">{item.reviewer?.username}</div>
                      <div className="text-sm text-muted-foreground">
                        {item.feedback_type} • {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  {item.comment && (
                    <p className="text-card-foreground mt-2">{item.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {trackId && <CommentSection trackId={trackId} />}

        {/* Share Dialog */}
        <ShareDialog
          isOpen={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          trackId={track?.id}
          trackTitle={track?.title}
          artistName={track?.artist_name}
        />
      </div>
    </div>
  );
}
